import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/api';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import toast from 'react-hot-toast';

// 원클릭 실행 기록 — Windows/Mac 원클릭이 끝날 때(실패·TV 없음 포함) 보낸 단계별 결과·기기 진단·로그 원문 (90일 보관)
const RESULT = {
  ok: { label: '성공', color: 'success' },
  partial: { label: '일부 실패', color: 'warning' },
  fail: { label: '실패', color: 'error' },
  no_devices: { label: 'TV 없음', color: 'default' },
  error: { label: '오류 중단', color: 'error' },
};
const STEP_LABEL = {
  connect: '연결·허용',
  screensaver: '화면보호기',
  identify: '기기 확인',
  mac_filter: 'MAC 필터',
  apk_download: 'APK 받기',
  install: '설치',
  wireguard: 'WireGuard',
  exception: '오류',
};

const fmt = (d) =>
  d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
const secs = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${Math.round(ms / 100) / 10}s`);
const who = (r) => (r.auth?.keyName ? r.auth.keyName : r.auth?.via === 'code' ? `현장 코드${r.auth.storeLabel ? ` · ${r.auth.storeLabel}` : ''}` : r.auth?.via === 'shared-key' ? '공용 키' : '코드 없음');

function RunDetail({ id, onClose }) {
  const [run, setRun] = useState(null);
  useEffect(() => {
    if (!id) return;
    setRun(null);
    api.get(`/field-kit/run-logs/${id}`).then((res) => setRun(res.data.run)).catch((e) => toast.error(e.response?.data?.message || 'Load failed'));
  }, [id]);
  const res = run ? RESULT[run.result] || { label: run.result, color: 'default' } : null;
  return (
    <Dialog open={Boolean(id)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        원클릭 실행 기록 {res && <Chip size="small" color={res.color} label={res.label} />}
      </DialogTitle>
      <DialogContent dividers>
        {!run ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {fmt(run.at)} · {run.os || '?'} · {run.launcher || 'direct'} · 모드 {run.mode || '—'} · {secs(run.durationMs)}
              <br />
              실행: <b>{who(run)}</b> {run.verified ? '' : '(미인증)'} · PC IP {run.clientIp || '—'} · 키트 {run.kitVersion || '—'}
              <br />
              스캔 {(run.subnets || []).join(', ') || '—'} · 찾은 TV {run.foundCount ?? '—'} · {run.adbVersion || ''}
            </Typography>
            {(run.devices || []).map((d, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  {d.deviceCode || '코드 없음'} · {d.ip} {d.result === 'ok' ? '✓' : `✗ (${STEP_LABEL[d.failedStep] || d.failedStep || '?'})`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {[d.manufacturer, d.model].filter(Boolean).join(' ') || '모델 —'} · {d.platform || d.chipset || '칩셋 —'} · Android {d.android || '—'} · {d.buildType || '—'}
                  {d.debuggable ? ` · debuggable=${d.debuggable}` : ''} · su {d.hasSu == null ? '—' : d.hasSu ? '있음' : '없음'} · root {d.rootMode || '—'}
                  <br />
                  앱 {d.appBefore || '—'} → {d.appAfter || '—'} · WG {d.wgIp || '연결 안 됨'}
                  {d.screensaver ? ` · 절전 ${Object.entries(d.screensaver).map(([k, v]) => `${k}=${v}`).join(' ')}` : ''}
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {(d.steps || []).map((s, j) => (
                    <Chip
                      key={j}
                      size="small"
                      variant={s.ok ? 'outlined' : 'filled'}
                      color={s.ok ? 'success' : 'error'}
                      label={`${STEP_LABEL[s.name] || s.name} ${secs(s.ms)}`}
                      title={s.msg || ''}
                    />
                  ))}
                </Stack>
                {(d.steps || []).filter((s) => s.msg && !s.ok).map((s, j) => (
                  <Typography key={j} variant="caption" color="error" display="block" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                    {STEP_LABEL[s.name] || s.name}: {s.msg}
                  </Typography>
                ))}
              </Paper>
            ))}
            <Typography sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>로그 원문 {run.logTruncated ? '(앞뒤만 보관)' : ''}</Typography>
            <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {run.log || '(없음)'}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

/** deviceCode 를 주면 그 기기 기록만 (기기 상세 "현장 기록" 탭) */
function RunLogPanel({ deviceCode = '', embedded = false }) {
  const [code, setCode] = useState(deviceCode);
  const [result, setResult] = useState('');
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (code.trim()) params.deviceCode = code.trim();
      if (result) params.result = result;
      const res = await api.get('/field-kit/run-logs', { params });
      setRuns(res.data.runs || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.message || 'Failed to load run logs');
    }
    setLoading(false);
  }, [code, result]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const body = (
    <>
      {!embedded && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <TextField size="small" label="기기 코드" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} sx={{ minWidth: 160 }} />
          <TextField size="small" select label="결과" value={result} onChange={(e) => setResult(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">전체</MenuItem>
            {Object.entries(RESULT).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <Button variant="outlined" onClick={load} disabled={loading}>조회</Button>
        </Stack>
      )}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760, '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>시각</TableCell>
              <TableCell>실행</TableCell>
              <TableCell>PC</TableCell>
              <TableCell>기기</TableCell>
              <TableCell>결과</TableCell>
              <TableCell>시간</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                  {loading ? '불러오는 중…' : '기록 없음'}
                </TableCell>
              </TableRow>
            )}
            {runs.map((r) => {
              const st = RESULT[r.result] || { label: r.result, color: 'default' };
              return (
                <TableRow key={r._id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r._id)}>
                  <TableCell>{fmt(r.at)}</TableCell>
                  <TableCell>
                    {r.os || '?'} · {r.mode || '—'}
                    <Typography variant="caption" color="text.secondary" display="block">{r.launcher || 'direct'}</Typography>
                  </TableCell>
                  <TableCell>
                    {who(r)}
                    {!r.verified && <Chip size="small" label="미인증" sx={{ ml: 0.5 }} />}
                    <Typography variant="caption" color="text.secondary" display="block">{r.clientIp || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    {(r.devices || []).map((d) => d.deviceCode || d.ip).join(', ') || '—'}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[...new Set((r.devices || []).map((d) => d.model).filter(Boolean))].join(', ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={st.color} label={st.label} />
                    <Typography variant="caption" color="text.secondary" display="block">{r.summary}</Typography>
                  </TableCell>
                  <TableCell>{secs(r.durationMs)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <RunDetail id={open} onClose={() => setOpen(null)} />
    </>
  );

  if (embedded) return body;
  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ManageSearchIcon sx={{ color: '#551FFF' }} />
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>원클릭 실행 기록 (분석용)</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Windows·Mac 원클릭이 끝날 때마다(실패·TV 못 찾음 포함) 보낸 단계별 결과, 기기 진단, 로그 원문입니다. 코드 없이 실행한 매장 PC 기록은 "미인증"으로 표시됩니다. 90일 보관.
      </Typography>
      {body}
    </Paper>
  );
}

export default RunLogPanel;
